import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { answerPharmacyIntakeFormQuestion } from '@api';
import {
  Field,
  Label,
  ErrorMessage,
  SearchAutocomplete,
  Paragraph,
  LocationDotIcon,
  PhoneIcon,
  EditIcon,
  showToast,
} from '@quickmd-llc/design-system';
import { PharmacyQuestion } from '@qmd-types/intakeForms';
import { SubmitButton } from './SubmitButton';
import { useIntakeForm } from '../../context/useIntakeForm';
import { useApplicationStore } from '@data';
import { Pharmacy } from '@qmd-types/patient';
import { searchPharmacies } from 'api/pharmacies';
import { formatAddress } from 'utils/misc/formatAddress';
import { formatPhoneNumber } from 'utils/misc/formatPhoneNumber';
import { VisitTypeIds } from '@router/loaders/utils';
import { Events, tracking } from '@lib';
import { useGetPharmaciesV2 } from 'api/generated/pharmacy-v2/pharmacy-v2';
import { PharmacyZipFilter } from '@quickmd-llc/design-system';

type Props = {
  question: PharmacyQuestion;
};

type FormValues = {
  pharmacyId: string;
};
type AutoCompleteItem = {
  data?: Pharmacy;
  label: string;
  value: string;
  key: string | number;
};

export function PharmacySelectionForm({ question }: Props) {
  const { patient } = useApplicationStore.getState();
  const { patientProfile: profile, visitDetail } = patient;
  const { pharmacy } = profile || {};
  const isPharmacySearchDaw = useApplicationStore((state) => state.flags.pharmacySearchDaw);
  const patientAddressZip = profile?.address?.zip ?? '';
  const [pharmacySearchZip, setPharmacySearchZip] = useState(patientAddressZip);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { handleNextStep, intakeForm, updateAnswer } = useIntakeForm();
  const { handleSubmit, formState, control, setValue } = useForm<FormValues>({
    defaultValues: {
      pharmacyId: question.updated
        ? question.pharmacyId
        : profile?.pharmacy?.pharmacyId || '',
    },
  });
  const [searchValue, setSearchValue] = useState(
    pharmacy ? `${pharmacy.name} - ${formatAddress(pharmacy.address)}` : '',
  );
  const [pharmacyList, setPharmacyList] = useState<AutoCompleteItem[]>([]);
  const [hasSelectedPharmacy, setHasSelectedPharmacy] = useState<boolean>(
    question.updated || false,
  );
  const [selectedPharmacy, setSelectedPharmacy] = useState<
    Pharmacy | undefined
  >(question.updated ? question : pharmacy || undefined);

  const [v2SearchDebounced, setV2SearchDebounced] = useState('');
  const v2Query = useGetPharmaciesV2(
    { SearchValue: v2SearchDebounced, ZipCode: pharmacySearchZip || patientAddressZip },
    {
      query: {
        enabled: Boolean(
          isPharmacySearchDaw && !selectedPharmacy && v2SearchDebounced.trim().length >= 3,
        ),
      },
    },
  );

  useEffect(() => {
    if (!isPharmacySearchDaw) return;
    if (searchValue.trim().length < 3) {
      setPharmacyList([]);
      setV2SearchDebounced('');
      return;
    }
    const timer = setTimeout(() => setV2SearchDebounced(searchValue), 500);
    return () => clearTimeout(timer);
  }, [searchValue, isPharmacySearchDaw]);

  useEffect(() => {
    if (!isPharmacySearchDaw || !v2Query.data) return;
    setPharmacyList(
      v2Query.data
        .filter((item) => Boolean(item.PharmacyId))
        .map((item) => {
          const data: Pharmacy = {
            pharmacyId: item.PharmacyId!,
            name: item.Name ?? '',
            phone: item.Phone ?? '',
            address: {
              streetAddress1: item.Address?.StreetAddress1 ?? '',
              streetAddress2: item.Address?.StreetAddress2 ?? undefined,
              city: item.Address?.City ?? '',
              stateCode: item.Address?.StateCode ?? '',
              zip: item.Address?.Zip ?? '',
              countryCode: item.Address?.CountryCode ?? '',
            },
            distanceMiles: item.DistanceMiles,
          };
          const label = `${data.name} - ${formatAddress(data.address)}`;
          return {
            data,
            key: data.pharmacyId,
            label,
            value: label,
          };
        }),
    );
  }, [v2Query.data, isPharmacySearchDaw]);

  useEffect(() => {
    if (!v2Query.isError) return;
    setPharmacyList([]);
    showToast({ type: 'error', message: 'Could not search pharmacies. Please try again.' });
  }, [v2Query.isError]);

  const { errors } = formState;
  const hasError = Boolean(errors.pharmacyId);

  async function onSubmit(data: FormValues) {
    const { intakeFormQuestionId } = question;
    const { intakeFormId } = intakeForm;

    setIsLoading(true);

    try {
      await answerPharmacyIntakeFormQuestion({
        intakeFormQuestionId,
        intakeFormId,
        data,
      });

      if (hasSelectedPharmacy) {
        tracking.trackEvent(Events.PATIENT_UPDATED_PREFERRED_PHARMACY, {
          pharmacyName: selectedPharmacy?.name || '',
          pharmacyId: selectedPharmacy?.pharmacyId || '',
          location: 'Intake Form',
        });
      }
      const updatedQuestion = {
        ...question,
        ...selectedPharmacy,
        updated: hasSelectedPharmacy,
      };

      updateAnswer(intakeFormQuestionId, updatedQuestion);
      handleNextStep();
    } catch {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }
  const onSelectPharmacy = useCallback(
    ({ data }: AutoCompleteItem) => {
      setValue('pharmacyId', data?.pharmacyId || '');
      setHasSelectedPharmacy(
        !pharmacy || data?.pharmacyId !== pharmacy.pharmacyId,
      );
      setSelectedPharmacy(data);
    },
    [pharmacy, setValue],
  );
  const onPharmacySearch = useCallback(
    async (search: string) => {
      if (isPharmacySearchDaw) return;
      if (searchValue.length < 3) return;
      try {
        const response = await searchPharmacies(search);

        setPharmacyList(
          response.map((item: Pharmacy) => ({
            data: item,
            key: item.pharmacyId,
            label: `${item.name} - ${formatAddress(item.address)}`,
            value: `${item.name} - ${formatAddress(item.address)}`,
          })),
        );
      } catch {
        setPharmacyList([]);
      }
    },
    [isPharmacySearchDaw, searchValue.length],
  );
  function getPharmacyListItemDisplay(
    pharmacyItem: AutoCompleteItem,
    handleSelect: () => void,
  ) {
    if (!pharmacyItem) return '';

    return (
      <Field className="cursor-pointer" onClick={handleSelect}>
        <div role="presentation" className="flex flex-row justify-between">
          <Label className="pointer-events-none">
            {pharmacyItem.data?.name}
          </Label>
          <Paragraph>
            {pharmacyItem.data?.distanceMiles?.toFixed(1)}mi
          </Paragraph>
        </div>
        <Paragraph>{formatAddress(pharmacyItem.data?.address)}</Paragraph>
      </Field>
    );
  }
  const clearPharmacy = useCallback(() => {
    setSelectedPharmacy(undefined);
    setValue('pharmacyId', '');
    setSearchValue('');
  }, [setValue]);
  return (
    <form
      className="flex flex-1 flex-col gap-sp-1"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Field>
        <Label>{question.question}</Label>
      </Field>
      {hasSelectedPharmacy &&
        selectedPharmacy &&
        visitDetail?.visitType.id === VisitTypeIds.AddictionTreatment && (
          <div className="mb-sp-2 rounded-lg border border-coastal-blue-50 bg-coastal-blue-10 p-sp-2">
            <Paragraph className="font-semibold">
              Check with your pharmacy
            </Paragraph>
            <Paragraph>
              To avoid potential delays, we recommend verifying with your
              pharmacy that your medication is in stock and that they accept
              telemedicine prescriptions for Suboxone.
            </Paragraph>
          </div>
        )}

      {selectedPharmacy ? (
        <div className="flex flex-col gap-sp-1 rounded bg-white p-sp-2">
          <div className="mb-sp-0.5 flex items-center justify-between">
            <Paragraph className="font-semibold">
              {selectedPharmacy.name}
            </Paragraph>
            <EditIcon
              className="cursor-pointer"
              iconSize="m"
              onClick={clearPharmacy}
            />
          </div>

          <div className="flex items-center gap-sp-1">
            <LocationDotIcon iconSize="l" />
            <Paragraph size="sm">
              {formatAddress(selectedPharmacy.address)}
            </Paragraph>
          </div>

          <div className="flex items-center gap-sp-1">
            <PhoneIcon iconSize="l" />
            <Paragraph size="sm">
              {formatPhoneNumber(selectedPharmacy.phone)}
            </Paragraph>
          </div>
        </div>
      ) : (
        <Controller
          control={control}
          name="pharmacyId"
          render={() => {
            return (
              <Field error={hasError}>
                {hasError && (
                  <ErrorMessage>{errors.pharmacyId?.message}</ErrorMessage>
                )}

                <SearchAutocomplete
                  className="w-full"
                  value={searchValue}
                  onChange={setSearchValue}
                  onResultSelect={onSelectPharmacy}
                  onSearch={onPharmacySearch}
                  searchResults={pharmacyList}
                  renderResult={getPharmacyListItemDisplay}
                  renderDropdownHeader={
                    isPharmacySearchDaw
                      ? () => (
                          <PharmacyZipFilter
                            zipCode={pharmacySearchZip}
                            patientZipFallback={patientAddressZip}
                            onSave={setPharmacySearchZip}
                            className="m-sp-2 bg-card-fillActive border-card-borderActive"
                          />
                        )
                      : undefined
                  }
                />
              </Field>
            );
          }}
          rules={{
            required: {
              value: true,
              message: 'A pharmacy is required',
            },
          }}
        />
      )}
      <SubmitButton isLoading={isLoading} />
    </form>
  );
}
